import { FC, useCallback, useContext, useEffect, useMemo } from "react";
import { Controller, useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import sanitize from "sanitize-html";
import { Option } from "space-monad";
import { mixed, number, object } from "yup";
import { NumberField } from "../../../../components/forms/number";
import { Select } from "../../../../components/forms/select";
import { ConfigContext } from "../../../../pages/token-onboarding/single/config-context";
import { keys, mapObject } from "../../../../utils/utils";
import { AllowedBlockchains } from "../../../chain/models/internal";
import { LaunchConfigurationsDTO } from "../dto";
import { Field } from "../internal/field";
import { Description, FieldHook, RenderFuncProps, Title } from "./common";

const Elem: FC<RenderFuncProps> = ({ item }) => {
  const { config } = useContext(ConfigContext);
  const { control, watch, setValue } = useFormContext();
  const amountField = useController({
    control,
    name: `${item.field}.amount`,
  });

  const schema = useSchema();
  const getSchemaCurried = useCallback(
    (item: Field.DefaultField) => schema(item, config),
    [config, schema]
  );

  const { t, i18n } = useTranslation("fields");
  const currentLanguage = useMemo(() => i18n.language.replaceAll("-", "_"), [
    i18n,
  ]);

  // defaultValues: useMemo(
  //   () => ({
  //     amount: 0,
  //     chain: config.configuration.cryptoChains
  //       ? keys(config.configuration.cryptoChains)[0]
  //       : undefined,
  //     currency: Option(config.configuration.cryptoChains)
  //       .map((x) => x[keys(x)[0]])
  //       .map((x) => x.pledgingInfo)
  //       .map((x) => keys(x)[0])
  //       .getOrElse(""),
  //   }),
  //   [config]
  // ),

  const chainOptions = useMemo(
    () =>
      config && config.configuration.cryptoChains
        ? mapObject(config.configuration.cryptoChains, (value, key) => ({
            title: key,
            value: key,
          }))
        : [],
    []
  );

  const chainValue = watch(`${item.field}.chain`);
  const currencyValue = watch(`${item.field}.currency`);

  const currencyOptions = useMemo(
    () =>
      Option(config)
        .map((x) => x.configuration.cryptoChains)
        .map((x) => x[chainValue as AllowedBlockchains])
        .map((x) => x.pledgingInfo)
        .map(keys)
        .map((x) =>
          x.map((y) => ({
            title: y,
            value: y,
          }))
        )
        .getOrElse([]),
    [chainValue, config]
  );

  useEffect(() => {
    setValue(`${item.field}.currency`, currencyOptions[0]?.value || "");
  }, [currencyOptions, item.field]);

  const amountLimits = useMemo(
    () =>
      Option.all([
        Option(currencyValue),
        Option(config)
          .map((x) => x.configuration.cryptoChains)
          .map((x) => x[chainValue as AllowedBlockchains])
          .map((x) => x.pledgingInfo),
      ]).map(([s, x]) => x[s]),
    [currencyValue, config, chainValue]
  );

  const amountLimitString = useMemo<Option<string>>(
    () =>
      amountLimits.map(({ amount: { maximum, minimum } }) =>
        t("min-max", { minimum, maximum })
      ),
    [amountLimits]
  );

  const title = useMemo(
    () =>
      Field.isPledgeField(item) && item.title?.[currentLanguage]
        ? item.title[currentLanguage]
        : undefined,
    [item, currentLanguage]
  );

  const description = useMemo(
    () =>
      Field.isPledgeField(item) && item.desc?.[currentLanguage]
        ? sanitize(item.desc[currentLanguage])
        : undefined,
    [item, currentLanguage]
  );

  return (
    <>
      {title && description ? (
        <>
          <Title>{title}</Title>
          <Description
            dangerouslySetInnerHTML={{ __html: description }}
          ></Description>
        </>
      ) : null}
      {title && !description ? (
        <>
          <Description></Description>
          <Title>{title}</Title>
        </>
      ) : null}
      {description && !title ? (
        <>
          <Description></Description>
          <Description
            dangerouslySetInnerHTML={{ __html: description }}
          ></Description>
        </>
      ) : null}
      <div>Pledge Chain</div>
      <Controller
        control={control}
        name={`${item.field}.chain`}
        render={({ field, fieldState: { error } }) => (
          <Select
            {...field}
            error={!!error}
            helperText={error?.message}
            options={chainOptions}
            fullWidth
            label={t("chain")}
          />
        )}
      />
      <div>Pledge Currency</div>
      <Controller
        control={control}
        name={`${item.field}.currency`}
        render={({ field, fieldState: { error } }) => (
          <Select
            options={currencyOptions}
            {...field}
            error={!!error}
            helperText={error?.message}
            fullWidth
            label={t("currency")}
          />
        )}
      />

      <div>Pledge Amount</div>
      <NumberField
        {...amountField.field}
        helperText={
          amountField.fieldState.error?.message || amountLimitString.get()
        }
        fullWidth
        type="text"
        label={t("amount")}
      />
    </>
  );
};

const useSchema = () => {
  const { t } = useTranslation("fields");

  return useCallback(
    (
      f: Field.DefaultField,
      config: LaunchConfigurationsDTO.Responses.GetSingle.T
    ) =>
      object()
        .shape({
          chain: config.configuration.cryptoChains
            ? mixed().oneOf(keys(config.configuration.cryptoChains))
            : mixed(),
          amount: number()
            .test(
              "is-too-large",
              t("amount-is-too-large"),
              (value, context) => {
                const limits = Option.all([
                  Option(context.parent.chain),
                  Option(context.parent.currency),
                  Option(config).map((x) => x.configuration.cryptoChains),
                ])

                  .map(([chain, currency, cryptoChains]) => [
                    cryptoChains[chain as AllowedBlockchains],
                    currency,
                  ])
                  .map(([x, _]) => [x.pledgingInfo, _])
                  .map(
                    ([pledgingInfo, currency]) =>
                      pledgingInfo[currency as string]
                  );

                return limits.fold(
                  () => true,
                  ({ amount: { maximum } }) => {
                    if (value === undefined) return true;
                    return value <= maximum;
                  }
                );
              }
            )
            .test(
              "is-too-small",
              t("amount-is-too-small"),
              (value, context) => {
                const limits = Option.all([
                  Option(context.parent.chain),
                  Option(context.parent.currency),
                  Option(config).map((x) => x.configuration.cryptoChains),
                ])

                  .map(([chain, currency, cryptoChains]) => [
                    cryptoChains[chain as AllowedBlockchains],
                    currency,
                  ])
                  .map(([x, _]) => [x.pledgingInfo, _])
                  .map(
                    ([pledgingInfo, currency]) =>
                      pledgingInfo[currency as string]
                  );

                return limits.fold(
                  () => true,
                  ({ amount: { minimum } }) => {
                    if (value === undefined) return true;
                    return value >= minimum;
                  }
                );
              }
            )
            .required(
              t("required-field", {
                ns: "errors",
                field: t("pledge-amount"),
              })
            )
            .label(t("pledge-amount")),
        })
        .required(
          t("required-field", {
            ns: "errors",
            field: t("pledge"),
          })
        )
        .label(t("pledge")),

    [t]
  );
};

export const usePledgeField: FieldHook = () => {
  const schema = useSchema();
  return useMemo(
    () => ({
      schema,
      elem: Elem,
      cryptoField: true,
    }),
    [schema]
  );
};
